import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import bboxPolygon from "@turf/bbox-polygon";
import bbox from "@turf/bbox";
import { SavedMapViewsSearchResult } from "@iventis/search/implementations/search-results-saved-map-views";
import { MapboxSearchResult } from "@iventis/search/implementations/search-results-mapbox";
import { MapObjectSearchResult } from "@iventis/search/implementations/search-results-map-objects";
import { SitemapSearchResult } from "@iventis/search/implementations/search-results-sitemaps";
import { SearchResultGroupInterface } from "@iventis/search/interface/search-result-interface";
import { SearchResultType, MapSearchResultType, MapboxLocationResult, SearchResultCallBacksType, SearchResultGetters } from "@iventis/search/types/types";
import { Content } from "@iventis/translations/content/typed-content";
import { SavedMapView } from "@iventis/domain-model/model/savedMapView";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { LayerStorageScope } from "@iventis/map-engine";
import React from "react";
import { mappingApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { isValidUuid } from "@iventis/utilities";
import { rootStore } from "./state/root.store";
import { closePopover, setMapView } from "./map-views.helpers";
import { map } from "./state/map.slice";

// need to pass the call backs as by default we want to zoom in to point, but with the route planner we want to pass the bound coords back
const callbacks = {
    savedMapViewsSearchResultCallback: (savedMapView: SavedMapView) => setMapView(savedMapView.id),
    mapboxSearchResultCallback: (mapboxLocation: MapboxLocationResult) => {
        // Mapbox search result is a bounding box, convert this a polygon as thats how we store bounds
        const polygon = bboxPolygon(mapboxLocation.bounds);
        rootStore.dispatch(map.actions.setBounds(polygon.geometry.coordinates));
        rootStore.dispatch(map.actions.setIsMapPanningAfterSearch(true));

        closePopover();
    },
    mapObjectSearchResultCallback: (mapObject: MapObject) => {
        // Creating bounding box around map object
        const boundingBox = bbox(mapObject.geoJsonFeature);
        // Convert the bounding box to a polygon as that is how we store bounds
        const polygon = bboxPolygon(boundingBox);
        rootStore.dispatch(map.actions.setBounds(polygon.geometry.coordinates));
        closePopover();
    },
    sitemapSearchResultCallback: (sitemap: Sitemap) => {
        const { sitemapConfigs } = rootStore.getState().mapReducer;
        const config = sitemapConfigs.find((c) => c.sitemapId === sitemap.id);

        let boundingCoordinates: number[][][] | undefined;

        // Use the configured version if available, preferring 2D over 3D
        if (config?.version2dId || config?.version3dId) {
            const versionId = config.version2dId ?? config.version3dId;
            const version = sitemap.versions?.find((v) => v.id === versionId);
            const level = version?.sitemapVersionLevels?.[0];
            boundingCoordinates = level?.bounding?.coordinates;
        }

        // Fall back to the first level (lowest level index) of the newest version
        if (!boundingCoordinates) {
            const sortedVersions = [...(sitemap.versions ?? [])].sort((a, b) => new Date(String(b.date)).getTime() - new Date(String(a.date)).getTime());
            for (const version of sortedVersions) {
                // Use the first level (index 0) as it represents the primary/ground-floor level for navigation
                const level = version.sitemapVersionLevels?.[0];
                if (level?.bounding?.coordinates) {
                    boundingCoordinates = level.bounding.coordinates;
                    break;
                }
            }
        }

        if (boundingCoordinates) {
            rootStore.dispatch(map.actions.setBounds(boundingCoordinates));
            rootStore.dispatch(map.actions.setIsMapPanningAfterSearch(true));
        }
        closePopover();
    },
};

/**
 *  Instantiate result groups, each one will be displayed when a user searches the map
 */

export const createSearch = (mapId: string, callbacks: SearchResultCallBacksType, getters: SearchResultGetters): SearchResultGroupInterface<MapSearchResultType>[] => [
    new SavedMapViewsSearchResult(
        translate(Content.map.search.saved_map_view),
        SearchResultType.SAVED_MAP_VIEW,
        <FontAwesomeIcon icon={["far", "camera"]} />,
        callbacks.savedMapViewsSearchResultCallback,
        {
            api: mappingApi,
            mapId,
        }
    ),
    new SitemapSearchResult(translate(Content.map.search.sitemaps), SearchResultType.SITEMAP, <FontAwesomeIcon icon={["far", "map"]} />, callbacks.sitemapSearchResultCallback, {
        getSitemaps: () => rootStore.getState().mapReducer.sitemaps,
    }),
    new MapboxSearchResult(
        translate(Content.map.search.locations),
        SearchResultType.LOCATION,
        <FontAwesomeIcon icon={["far", "location-dot"]} />,
        callbacks.mapboxSearchResultCallback,
        {
            limit: 3,
            types: ["country", "region", "postcode", "district", "place", "locality", "neighborhood", "address", "poi"],
            mapboxSearchApiUrl: "https://api.mapbox.com/geocoding/v5/mapbox.places",
            getMapPosition: () => {
                const { position } = rootStore.getState().mapReducer.mapModule;
                return [position.value.lng, position.value.lat];
            },
        }
    ),
    new MapObjectSearchResult(
        translate(Content.map.search.objects),
        SearchResultType.MAP_OBJECT,
        <FontAwesomeIcon icon={["far", "cube"]} />,
        mapId,
        callbacks.mapObjectSearchResultCallback,
        {
            api: mappingApi,
            getLayers: () => {
                const { mapModule } = rootStore.getState().mapReducer;
                const mapLayers = mapModule.layers.value.filter((layer) => layer.visible && layer.storageScope === LayerStorageScope.LocalAndTiles && isValidUuid(layer.id));
                return mapLayers.map((layer) => ({ id: layer.id, thumbnailUrl: getters.getMapLayerPreviewUrl(layer).then((url) => url) }));
            },
        }
    ),
];

export const createSearchResultGroups = (mapId: string, getters: SearchResultGetters) => createSearch(mapId, callbacks, getters);
